import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { getSystemTrades, SystemTradeItem, SystemTradesParams } from '../../api/tax';
import { useTaxYearStore } from '../../stores/useTaxYearStore';
import { useAutoRefreshTrigger } from '../../stores/useGlobalAutoRefreshStore';
import { formatUsd, formatCrypto, formatTimestamp, formatOrderId } from './taxReportingUtils';
import DataTable, { DataTableFilter } from '../shared/DataTable';
import { usePortfolioRegistry } from '../../hooks/usePortfolioRegistry';

// Server-paginated. See CoinbaseTransactionsView.tsx for the canonical idiom.

export const SystemTradesView: React.FC = () => {
  const taxYear = useTaxYearStore((s) => s.selectedYear);
  const refreshTrigger = useAutoRefreshTrigger('tax-reporting');

  const [portfolioId, setPortfolioId] = useState('');
  const [productId, setProductId] = useState('');
  const [tradeGroupType, setTradeGroupType] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'created_at', desc: true }]);
  const sortBy = sorting[0]?.id;
  const sortDir: 'asc' | 'desc' | undefined = sorting[0] ? (sorting[0].desc ? 'desc' : 'asc') : undefined;


  const onFilterChange = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setPageIndex(0);
  };

  const params: SystemTradesParams = {
    taxYear,
    portfolioId: portfolioId || undefined,
    productId: productId || undefined,
    tradeGroupType: tradeGroupType || undefined,
    sortBy,
    sortDir,
    page: pageIndex + 1,
    pageSize,
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['tax', 'system-trades', taxYear, portfolioId, productId, tradeGroupType, sortBy, sortDir, pageIndex, pageSize, refreshTrigger],
    queryFn: () => getSystemTrades(params),
  });

  const items = data?.items ?? [];
  const total = data?.metadata.total ?? 0;
  const pageCount = data ? Math.max(1, Math.ceil(total / pageSize)) : -1;

  const optionList = (values: string[]) =>
    Array.from(new Set(values)).sort().map((v) => ({ value: v, label: v }));

  const { options: portfolioOptions } = usePortfolioRegistry();
  const productOptions = useMemo(() => optionList(items.map((i) => i.product_id)), [items]);
  const tradeGroupOptions = useMemo(() => optionList(items.map((i) => i.trade_group_type)), [items]);

  const columns = useMemo<ColumnDef<SystemTradeItem>[]>(
    () => [
      {
        // accessorKey reads the value from `executed_at` on the response row;
        // the column `id` is `created_at` to match the backend sort allowlist
        // (the API resolves `executed_at` from the underlying `created_at` DB column).
        id: 'created_at',
        accessorKey: 'executed_at',
        header: 'Executed',
        cell: (info) => <span className="date-cell">{formatTimestamp(info.getValue() as string | null)}</span>,
      },
      { accessorKey: 'source_environment', header: 'Env' },
      { accessorKey: 'portfolio_id', header: 'Portfolio' },
      { accessorKey: 'product_id', header: 'Product' },
      {
        accessorKey: 'trade_side',
        header: 'Side',
        cell: (info) => (
          <span className={`badge badge-${(info.getValue() as string).toLowerCase()}`}>
            {info.getValue() as string}
          </span>
        ),
      },
      { accessorKey: 'status', header: 'Status' },
      { accessorKey: 'trade_group_type', header: 'Trade Group Type' },
      { accessorKey: 'trade_group_id', header: 'Group ID' },
      { accessorKey: 'quantity', header: 'Quantity', cell: (info) => formatCrypto(info.getValue() as string) },
      {
        accessorKey: 'usd_value',
        header: 'USD Value',
        cell: (info) => formatUsd(info.getValue() as string | null),
      },
      {
        accessorKey: 'client_order_id',
        header: 'Client Order ID',
        cell: (info) => <span className="order-id-cell">{formatOrderId(info.getValue() as string | null)}</span>,
        enableSorting: false,
      },
    ],
    []
  );

  const filters: DataTableFilter[] = [
    { id: 'portfolio_id', label: 'Portfolio', value: portfolioId, options: portfolioOptions, onChange: onFilterChange(setPortfolioId) },
    { id: 'product_id', label: 'Product', value: productId, options: productOptions, onChange: onFilterChange(setProductId) },
    { id: 'trade_group_type', label: 'Trade Group', value: tradeGroupType, options: tradeGroupOptions, onChange: onFilterChange(setTradeGroupType) },
  ];

  return (
    <div aria-label="System Trades view">
      <DataTable<SystemTradeItem>
        data={items}
        columns={columns}
        serverSorting={{
          state: sorting,
          onChange: (s) => {
            setSorting(s);
            setPageIndex(0);
          },
        }}
        filters={filters}
        loadingMessage="Loading system trades…"
        isLoading={isLoading}
        isError={isError}
        errorMessage={error instanceof Error ? error.message : 'Failed to load system trades'}
        emptyMessage="No system trades for these filters."
        serverPagination={{
          pageIndex,
          pageSize,
          pageCount,
          totalRows: total,
          onPageIndexChange: setPageIndex,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setPageIndex(0);
          },
        }}
      />
    </div>
  );
};

export default SystemTradesView;
